import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { NostrEvent } from '@nostrify/nostrify';
import { useAuthor } from '@/hooks/useAuthor';
import { Skeleton } from '@/components/ui/skeleton';
import {
  extractThumbnail,
  extractTitle,
  extractDuration,
  extractVideoUrl,
  extractIsNsfw,
  formatDuration,
  formatTimeAgo,
} from '@/hooks/useVideoFeed';
import { useVideoThumbnail } from '@/hooks/useVideoThumbnail';
import { isYouTubeUrl, extractYouTubeVideoId, getYouTubeThumbnailUrl } from '@/lib/youtube';
import { YouTubeIcon } from '@/components/YouTubeEmbed';

interface ShortCardProps {
  event: NostrEvent;
  /** Called on mobile when card is tapped — opens the fullscreen viewer */
  onMobileClick?: () => void;
}

export function ShortCard({ event, onMobileClick }: ShortCardProps) {
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;
  const [nsfwRevealed, setNsfwRevealed] = useState(false);

  const title = extractTitle(event);
  const storedThumbnail = extractThumbnail(event);
  const videoUrl = extractVideoUrl(event);
  const duration = extractDuration(event);
  const isNsfw = extractIsNsfw(event);
  const timeAgo = formatTimeAgo(event.created_at);
  const displayName = metadata?.name ?? metadata?.display_name ?? event.pubkey.slice(0, 8) + '…';

  // YouTube bridge: use YT thumbnail CDN as fallback
  const isYtVideo = videoUrl ? isYouTubeUrl(videoUrl) : false;
  const ytVideoId = isYtVideo && videoUrl ? extractYouTubeVideoId(videoUrl) : null;
  const ytFallbackThumb = ytVideoId ? getYouTubeThumbnailUrl(ytVideoId) : undefined;

  const thumbnail = useVideoThumbnail(event.id, storedThumbnail ?? ytFallbackThumb, isYtVideo ? undefined : videoUrl);

  const videoHref = `/video/${event.id}`;
  const authorHref = `/profile/${event.pubkey}`;

  return (
    <div
      className="group shrink-0 snap-start"
      style={{ width: 160 }}
    >
      {/* Portrait thumbnail — 9:16 */}
      <Link
        to={onMobileClick ? '#' : videoHref}
        onClick={onMobileClick ? (e) => { e.preventDefault(); onMobileClick(); } : undefined}
        className="relative block rounded-xl overflow-hidden"
        style={{
          paddingBottom: '177.78%', // 9:16
          background: 'var(--bg-secondary)',
          display: 'block',
        }}
      >
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            style={isNsfw && !nsfwRevealed ? { filter: 'blur(16px)', transform: 'scale(1.08)' } : undefined}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            {/* Shimmer while frame is being captured */}
            <div className="absolute inset-0 animate-pulse" style={{ background: 'var(--bg-secondary)' }} />
            <svg
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="relative"
              style={{ color: 'var(--gb-border)', opacity: 0.5 }}
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        )}

        {/* NSFW overlay */}
        {isNsfw && !nsfwRevealed && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 cursor-pointer"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={e => { e.preventDefault(); setNsfwRevealed(true); }}
          >
            <span className="text-2xl">🔞</span>
            <span className="text-white text-[10px] font-bold uppercase tracking-widest">NSFW</span>
            <span className="text-white/70 text-[10px]">Tap to reveal</span>
          </div>
        )}

        {/* YouTube source badge */}
        {isYtVideo && (
          <span
            className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded font-bold text-white"
            style={{ background: '#ff0000' }}
          >
            <YouTubeIcon size={9} />
            YT
          </span>
        )}

        {/* Duration badge */}
        {duration !== undefined && (
          <span
            className="absolute bottom-2 right-2 text-xs px-1.5 py-0.5 rounded text-white font-mono"
            style={{ background: 'rgba(0,0,0,0.75)' }}
          >
            {formatDuration(duration)}
          </span>
        )}

        {/* Gradient + info overlay — always visible at bottom */}
        <div
          className="absolute inset-x-0 bottom-0 px-2 pb-2 pt-8"
          style={{
            background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)',
          }}
        >
          <p
            className="text-white text-xs font-semibold leading-tight"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            } as React.CSSProperties}
          >
            {title}
          </p>
        </div>

        {/* Play overlay on hover */}
        <div
          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ background: 'rgba(0,0,0,0.25)' }}
        >
          <div className="bg-white/90 rounded-full p-3">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="translate-x-0.5 text-black"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      </Link>

      {/* Author below card */}
      <Link
        to={authorHref}
        className="block mt-1.5 text-xs truncate no-underline hover:underline"
        style={{ color: 'var(--text-secondary)' }}
      >
        {displayName}
      </Link>
      <p className="text-xs" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
        {timeAgo}
      </p>
    </div>
  );
}

export function ShortCardSkeleton() {
  return (
    <div className="shrink-0" style={{ width: 160 }}>
      <Skeleton
        className="rounded-xl w-full"
        style={{ paddingBottom: '177.78%', background: 'var(--bg-secondary)' }}
      />
      <Skeleton className="h-3 w-20 mt-2" style={{ background: 'var(--bg-secondary)' }} />
      <Skeleton className="h-3 w-12 mt-1" style={{ background: 'var(--bg-secondary)' }} />
    </div>
  );
}
